'use client';

import { FC } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { DynamicPageContentType } from '@ravago/shared/page-data/models/contentful';
import { NormalizedNavigationDropdown } from '@ravago/shared/page-data/models/contentful-mapped';
import { DataTestSelector } from '@ravago/shared/page-elements-radiance/utils/format/format.utils';
import { NavigationUtils } from '@ravago/shared/page-elements-radiance/utils/navigation/navigation.utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { NavItem } from '@ravago/shared/radiance/components/navigation/components/NavItem/NavItem';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';

interface Props {
  menu?: NormalizedNavigationDropdown;
  locale: string;
  colorIndex: number;
  dynamicPageUrls?: { [key in DynamicPageContentType]?: string };
  onNavigate: () => void;
}

const MegaMenu: FC<Props> = ({ menu, locale, colorIndex, dynamicPageUrls, onNavigate }) => {
  const { user } = useAuthentication();

  if (!menu) return null;

  return (
    <BreakpointSwitch includeViews={['desktop']}>
      <Box spacing={{ x: 'lg' }} gap="xl" width="100%" direction="column">
        <Box gap="xl" width="100%">
          {NavigationUtils.filterItemsByAccessLevel(menu.items, user).map((item) => (
            <Box width="100%" direction="column" key={item.label}>
              {'items' in item ? (
                <Box direction="column">
                  <Typography variant="heading-4" color="strong">
                    {item.label}
                  </Typography>
                  <Box direction="column" gap="sm">
                    {NavigationUtils.filterItemsByAccessLevel(item.items, user).map((subItem) => (
                      <NavItem
                        attributes={{ ...DataTestSelector('mega-menu-item', subItem.label) }}
                        href={NavigationUtils.buildRoute(locale, subItem, dynamicPageUrls)}
                        key={subItem.label}
                        color={colorIndex}
                        target={subItem.openInNewTab ? '_blank' : undefined}
                        onClick={onNavigate}
                        icon={subItem.icon ?? undefined}
                        trailingIcon={subItem.openInNewTab ? 'external-link' : undefined}
                      >
                        <Typography variant="body" color="strong">
                          {subItem.label}
                        </Typography>
                        {subItem.description && (
                          <Typography variant="small" color="subtle">
                            {subItem.description}
                          </Typography>
                        )}
                      </NavItem>
                    ))}
                  </Box>
                </Box>
              ) : (
                <NavItem
                  attributes={{ ...DataTestSelector('mega-menu-item', item.label) }}
                  href={NavigationUtils.buildRoute(locale, item, dynamicPageUrls)}
                  key={item.label}
                  target={item.openInNewTab ? '_blank' : undefined}
                  color={colorIndex}
                  onClick={onNavigate}
                  icon={item.icon ?? undefined}
                  trailingIcon={item.openInNewTab ? 'external-link' : undefined}
                >
                  <Typography variant="body" color="strong">
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Typography variant="small" color="subtle">
                      {item.description}
                    </Typography>
                  )}
                </NavItem>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </BreakpointSwitch>
  );
};

export default MegaMenu;
